import React, { useState, useRef, useEffect } from 'react';
// Import the video directly using webpack - this might fail if webpack isn't configured for mp4
let aiRecipeVideo;
try {
  aiRecipeVideo = require('../components/WhatTomakeIntro.mp4');
} catch (e) {
  console.warn('Failed to import video directly, will use path-based fallback');
  aiRecipeVideo = null;
}

const VideoPlayer = ({ src, poster = null, className = '', onPlay = () => {}, onPause = () => {} }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [showPlayButton, setShowPlayButton] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  // Detect mobile devices
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = window.innerWidth <= 768;
      setIsMobile(isMobileDevice);
      // Set initial zoom level for mobile devices
      setZoomLevel(isMobileDevice ? 1.2 : 1);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // State to track which path we're currently trying
  const [currentPathIndex, setCurrentPathIndex] = useState(0);
  
  // Possible paths to try
  const possiblePaths = [
    '/WhatTomakeIntro.mp4', // From public folder - this should work
    `${process.env.PUBLIC_URL}/WhatTomakeIntro.mp4`, // With PUBLIC_URL prefix
    src, // Original path
    '../components/WhatTomakeIntro.mp4', // Relative from components
    './WhatTomakeIntro.mp4', // Same directory
    '/my-recipe-app/client1/src/components/WhatTomakeIntro.mp4' // Full path
  ];

  // Auto-hide controls on mobile
  useEffect(() => {
    if (isPlaying && isMobile) {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    } else {
      setShowControls(true);
    }

    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [isPlaying, isMobile]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => {
      setIsPlaying(true);
      setShowPlayButton(false);
      onPlay();
    };

    const handlePause = () => {
      setIsPlaying(false);
      setShowPlayButton(true);
      onPause();
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setShowPlayButton(true);
      onPause();
    };
    
    const handleError = (error) => {
      console.error('Video error:', error);
      console.error('Failed to load video from path:', video.src);
      
      // Try the next path if we have more to try
      if (currentPathIndex < possiblePaths.length - 1) {
        console.log(`Trying next path: ${possiblePaths[currentPathIndex + 1]}`);
        setCurrentPathIndex(currentPathIndex + 1);
        // No need to set error state here as we're trying another path
      } else {
        // We've tried all paths, show error
        setHasError(true);
        setIsPlaying(false);
        setShowPlayButton(false);
      }
    };
    
    const handleLoadedData = () => {
      console.log('Video loaded successfully from:', video.src);
      setIsLoaded(true);
      setDuration(video.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
    };

    // Set the video source based on current path index
    if (src.includes('WhatTomakeIntro.mp4') && !aiRecipeVideo) {
      video.src = possiblePaths[currentPathIndex];
    }

    // Add event listeners
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('error', handleError);
    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);

    // Clean up event listeners
    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('error', handleError);
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [onPlay, onPause, src, currentPathIndex, possiblePaths]);

  // Fullscreen functionality
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      
      console.log('Fullscreen change detected:', isCurrentlyFullscreen);
      setIsFullscreen(isCurrentlyFullscreen);
    };

    const handleKeydown = (e) => {
      // Handle ESC key to exit fullscreen
      if (e.key === 'Escape' && isFullscreen) {
        console.log('ESC pressed, exiting fullscreen');
        setIsFullscreen(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    document.addEventListener('keydown', handleKeydown);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [isFullscreen]);

  const togglePlay = () => {
    if (hasError) return;
    
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      video.play().catch(error => {
        console.error('Failed to play video:', error);
        setHasError(true);
      });
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const handleSeek = (e) => {
    const video = videoRef.current;
    if (!video) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const seekTime = (clickX / width) * duration;
    video.currentTime = seekTime;
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Handle zoom controls
  const increaseZoom = () => {
    if (zoomLevel < 2.5) {
      setZoomLevel(prev => Math.min(prev + 0.2, 2.5));
    }
  };

  const decreaseZoom = () => {
    if (zoomLevel > 0.8) {
      setZoomLevel(prev => Math.max(prev - 0.2, 0.8));
    }
  };

  const resetZoom = () => {
    setZoomLevel(isMobile ? 1.2 : 1);
  };

  // Handle fullscreen toggle
  const toggleFullscreen = () => {
    const container = containerRef.current;
    const video = videoRef.current;
    if (!container || !video) return;

    // Check if we're already in native fullscreen
    const isCurrentlyNativeFullscreen = !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
    );

    console.log('Toggle fullscreen:', { 
      isFullscreen, 
      isCurrentlyNativeFullscreen 
    });

    if (!isFullscreen) {
      // Try to enter fullscreen
      const enterFullscreen = async () => {
        try {
          // Try video element first (works better on mobile)
          if (video.requestFullscreen) {
            await video.requestFullscreen();
          } else if (video.webkitRequestFullscreen) {
            await video.webkitRequestFullscreen();
          } else if (video.mozRequestFullScreen) {
            await video.mozRequestFullScreen();
          } else if (video.msRequestFullscreen) {
            await video.msRequestFullscreen();
          } else if (container.requestFullscreen) {
            await container.requestFullscreen();
          } else if (container.webkitRequestFullscreen) {
            await container.webkitRequestFullscreen();
          } else if (container.mozRequestFullScreen) {
            await container.mozRequestFullScreen();
          } else if (container.msRequestFullscreen) {
            await container.msRequestFullscreen();
          } else {
            // Fallback: simulate fullscreen for mobile
            console.log('Fullscreen API not supported, using fallback');
            setIsFullscreen(true);
          }
        } catch (error) {
          console.error('Fullscreen error:', error);
          // Fallback: simulate fullscreen for mobile
          console.log('Using fallback fullscreen mode');
          setIsFullscreen(true);
        }
      };

      enterFullscreen();
    } else {
      // Exit fullscreen
      if (isCurrentlyNativeFullscreen) {
        // Exit native fullscreen
        try {
          if (document.exitFullscreen) {
            document.exitFullscreen();
          } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
          } else if (document.mozCancelFullScreen) {
            document.mozCancelFullScreen();
          } else if (document.msExitFullscreen) {
            document.msExitFullscreen();
          }
          // Don't set state here - let the event listener handle it
        } catch (error) {
          console.error('Exit fullscreen error:', error);
          setIsFullscreen(false);
        }
      } else {
        // Exit fallback fullscreen
        console.log('Exiting fallback fullscreen mode');
        setIsFullscreen(false);
      }
    }
  };

  // Show controls on touch for mobile
  const handleTouchStart = () => {
    if (isMobile) {
      setShowControls(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      controlsTimeoutRef.current = setTimeout(() => {
        if (isPlaying) {
          setShowControls(false);
        }
      }, 3000);
    }
  };

  // Handle container click - for exiting fullscreen
  const handleContainerClick = (e) => {
    // Only handle clicks on the container itself, not on controls or video
    if (e.target === e.currentTarget && isFullscreen) {
      console.log('Container clicked while in fullscreen, attempting to exit');
      toggleFullscreen();
    }
  };

  // Apply custom zoom level through inline style
  const videoStyle = {
    transform: `scale(${zoomLevel})`,
  };

  return (
    <div 
      className={`video-player-container ${className} ${isFullscreen ? 'fullscreen' : ''} ${!isPlaying && !hasError ? 'show-peach-background' : ''}`} 
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onClick={handleContainerClick}
    >
      <video
        ref={videoRef}
        className="video-element"
        src={aiRecipeVideo || (src.includes('WhatTomakeIntro.mp4') ? possiblePaths[currentPathIndex] : src)}
        poster={poster}
        preload="metadata"
        playsInline
        style={videoStyle}
        onClick={(e) => {
          e.stopPropagation(); // Prevent triggering container click
          togglePlay();
        }}
      />
      
      {/* Peach background overlay when not playing */}
      {!isPlaying && !hasError && (
        <div className="peach-background-overlay" onClick={(e) => {
          e.stopPropagation(); // Prevent triggering container click
          togglePlay();
        }}>
          <div className="demo-placeholder">
            <div className="demo-play-icon">
              <svg className="w-8 h-8 text-orange-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
            </div>
            <p className="text-gray-600 font-body">Tap to watch the demo</p>
          </div>
        </div>
      )}
      
      {hasError ? (
        <div className="video-error">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="error-icon">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
          </svg>
          <p>Video could not be loaded</p>
          <p className="video-error-path">
            Tried all paths: {possiblePaths.join(', ')}
          </p>
        </div>
      ) : (
        <>
          {/* Video Controls Overlay */}
          {isLoaded && showControls && (
            <div className="video-controls-overlay" onClick={(e) => e.stopPropagation()}>
              {/* Progress Bar */}
              <div className="progress-bar-container" onClick={handleSeek}>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${(currentTime / duration) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Bottom Controls */}
              <div className="bottom-controls">
                <div className="left-controls">
                  <button 
                    className="control-button play-pause-button" 
                    onClick={togglePlay}
                    aria-label={isPlaying ? "Pause" : "Play"}
                  >
                    {isPlaying ? (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    )}
                  </button>
                  
                  <button 
                    className="control-button mute-button" 
                    onClick={toggleMute}
                    aria-label={isMuted ? "Unmute" : "Mute"}
                  >
                    {isMuted ? (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                      </svg>
                    )}
                  </button>
                  
                  <span className="time-display">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                </div>
                
                <div className="right-controls">
                  <button 
                    className="control-button fullscreen-button" 
                    onClick={toggleFullscreen}
                    aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                  >
                    {isFullscreen ? (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
          
          
          {/* Zoom controls - visible when video is loaded and not in fullscreen */}
          {isLoaded && !isFullscreen && (
            <div className="zoom-controls" onClick={(e) => e.stopPropagation()}>
              <button 
                className="zoom-button zoom-in" 
                onClick={increaseZoom}
                aria-label="Zoom in"
                disabled={zoomLevel >= 2.5}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                </svg>
              </button>
              <button 
                className="zoom-button zoom-out" 
                onClick={decreaseZoom}
                aria-label="Zoom out"
                disabled={zoomLevel <= 0.8}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 13H5v-2h14v2z"/>
                </svg>
              </button>
              <button 
                className="zoom-button zoom-reset" 
                onClick={resetZoom}
                aria-label="Reset zoom"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
                </svg>
              </button>
            </div>
          )}
        </>
      )}
      
      {!isLoaded && !hasError && (
        <div className="video-loading">
          <div className="loading-spinner"></div>
          <p>Loading video{currentPathIndex > 0 ? ` (attempt ${currentPathIndex + 1}/${possiblePaths.length})` : ''}...</p>
        </div>
      )}
    </div>
  );
};

export default VideoPlayer; 